<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :maxBlocksInCache="null"
        >
           <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>  
            <b-button-group class="d-none d-lg-flex mr-1">
                <properties-search-multiselect
                    v-model="propertiesSelected"
                    variant="primary"
                    :properties="properties"
                ></properties-search-multiselect>
            </b-button-group>
             <b-button-group aria-label="Export table data">
                <responsive-button
                    :icon="['fas', 'file-excel']"
                    :href="exportExcelHref"
                    @click="generatingExcelReportSuccessToast"
                    variant="primary"
                    ></responsive-button
                >
            </b-button-group>
            <responsive-button
                :icon="['fas', 'arrows-rotate']"
                @click="refresh"
                variant="primary"
                >Refresh</responsive-button
            >
            <responsive-button
                :icon="['fas', 'plus']"
                variant="primary"
                @click="handleCreateRentalExpenseClick"
                >Add Rental Expense</responsive-button
            >
            <b-button-group class="d-lg-none">
                <more-filters-dropdown>
                    <template
                        v-slot:default="{
                            handleDropdownShow,
                            handleDropdownHide,
                        }"
                    >
                        <properties-search-multiselect
                            v-model="propertiesSelected"
                            variant="primary"
                            :properties="properties"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                            class="d-flex d-lg-none rental-expense-select"
                        ></properties-search-multiselect>
                    </template>
                </more-filters-dropdown>
            </b-button-group>
        </grid>
        <rental-expense-details-modal-form
            :modal-id="rentalExpenseCreateModalID"
            :key="rentalExpenseCreateModalKey"
            :create="createRentalExpense"
            @success-save="refresh"
        />
        <rental-expense-details-modal-form
            :modal-id="rentalExpenseUpdateModalID"
            :initial-values="currentRentalExpenseInitialValues"
            :update="updateRentalExpense"
            @success-save="refresh"
        />
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import API from '../../../api';
    import { escapeHTML } from '../../../utils/strings';
    import DateRangePicker, {
        ALL_DATES,
        LAST_MONTH,
        LAST_YEAR,
        nameToRange,
        THIS_MONTH,
        THIS_YEAR,
        TODAY,
        YESTERDAY,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
    import Grid from '../../generic/grid/grid.vue';
    import Modal from '../../generic/modal/modal.vue';
    import Page from '../../generic/page/page.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import PropertiesSearchMultiselect from '../../generic/properties-search-multiselect/properties-search-multiselect.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import RentalExpenseDetailsModalForm from './rental-expense-details-modal-form.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';

    export default {
        name: 'RentalExpenseList',
        components: {
            DateRangePicker,
            SearchMultiselect,
            Grid,
            Modal,
            Page,
            RentalExpenseDetailsModalForm,
            PropertiesSearchMultiselect,
            ResponsiveButton,
            MoreFiltersDropdown,
            ResponsiveDropdown,
            actionsCellRenderer: ActionsCellRenderer,
        },
        mixins: [removeScrollMixin],
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    return API.get('/app/rental-expense/rental-expense/grid/', {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            start_date: this.startDateRequest,
                            end_date: this.endDateRequest,
                            properties_and_rooms: this.propertiesAndRoomsRequest,
                        },
                    });
                };
            },
            exportExcelHref() {
                return (
                    `/app/rental-expense/rental-expense/excel/?offset=${this.offset}&limit=${this.limit}` +
                    `&sort=${this.sort}&desc=${this.desc}&start_date=${this.startDateRequest}` +
                    `&end_date=${this.endDateRequest}&properties_and_rooms=${this.propertiesAndRoomsRequest}` +
                    `&search=${this.search}`
                );
            },
            propertiesAndRoomsRequest() {
                const propertiesAndRooms = new Set();
                this.propertiesSelected.forEach((property) => {
                    propertiesAndRooms.add(property.property_id);
                    if (!!property.room_id) {
                        propertiesAndRooms.add(-property.room_id);
                    }
                });
                return [...propertiesAndRooms].join(',');
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            currentRentalExpenseInitialValues() {
                if (!!this.currentRentalExpense) {
                    return {
                        id: this.currentRentalExpense.id,
                        date: this.currentRentalExpense.date,
                        amount: this.currentRentalExpense.amount,
                        account_id: this.currentRentalExpense.account_id,
                        vendor_id: this.currentRentalExpense.vendor_id,
                        assignee: this.currentRentalExpense.assignee,
                        description: this.currentRentalExpense.description,
                        properties: this.currentRentalExpense.properties.map(
                            (property) => ({
                                property_id: property.property_id,
                                room_id: property.room_id,
                            })
                        ),
                    };
                }
            },
        },
        mounted() {
            (async () => {
                this.busy = true;
                try {
                    await this.fetchProperties();
                } finally {
                    this.busy = false;
                }
            })();
        },
        data() {
            return {
                busy: false,
                tabs: [
                    {
                        title: 'One-Time',
                        route: '/app/rental-expense/list/',
                        active: true,
                    },
                    {
                        title: 'Recurring',
                        route: '/app/rental-expense/recurring/list/',
                        active: false,
                    },
                ],
                rentalExpenseCreateModalID: 'rental-expense-create-modal',
                rentalExpenseCreateModalKey: 0,
                rentalExpenseUpdateModalID: 'rental-expense-update-modal',
                currentRentalExpense: null,
                dateRange: nameToRange(THIS_MONTH),
                dateRequestFormat: 'yyyy-MM-dd',
                predefinedRanges: [
                    TODAY,
                    YESTERDAY,
                    THIS_MONTH,
                    LAST_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                    ALL_DATES,
                ],
                properties: [],
                propertiesSelected: [],
                columnDefs: [
                    {
                        field: 'id',
                        type: 'idColumn',
                        maxWidth: 110,
                    },
                    {
                        field: 'date',
                        type: 'dateColumn',
                        headerName: 'Date',
                        minWidth: 120,
                    },
                    {
                        field: 'properties',
                        headerName: 'Properties',
                        autoHeight: true,
                        minWidth: 320,
                        cellRenderer: ({ data }) =>
                            data &&
                            data.properties
                                .map((property) => escapeHTML(property.name))
                                .join('<br />'),
                    },
                    {
                        field: 'description',
                        headerName: 'Description',
                    },
                    {
                        field: 'amount',
                        headerName: 'Amount',
                    },
                    {
                        field: 'assignee_name',
                        headerName: 'Assignee',
                    },
                    {
                        headerName: '',
                        maxWidth: 130,
                        minWidth: 130,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            update: (rentalExpense) => {
                                this.currentRentalExpense = rentalExpense;
                                this.$bvModal.show(this.rentalExpenseUpdateModalID);
                            },
                            delete: async (rentalExpense) => {
                                try {
                                    await API.delete(
                                        `/app/rental-expense/rental-expense/${rentalExpense.id}/`
                                    );
                                } catch (error) {
                                    this.errorToast({
                                        text: `Could not delete rental expense.`,
                                    });
                                    return;
                                }
                                this.refresh();
                            },
                        },
                    },
                ],
                offset: 0,
                limit: 20,
                sort: 'id',
                desc: false,
                search: '',
            };
        },
        methods: {
            async fetchProperties() {
                try {
                    const response = await API.get(
                        `/app/rental-expense/rental-expense/properties/`
                    );
                    this.properties = response.data;
                } catch {
                    this.errorToast({ text: `Could not load properties.` });
                }
            },
            handleCreateRentalExpenseClick() {
                this.rentalExpenseCreateModalKey += 1;
                this.$nextTick(() => {
                    this.$bvModal.show(this.rentalExpenseCreateModalID);
                });
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.refresh();
            },
            createRentalExpense(payload) {
                return API.post(`/app/rental-expense/rental-expense/`, payload);
            },
            updateRentalExpense({ id, ...payload }) {
                return API.patch(
                    `/app/rental-expense/rental-expense/${id}/`,
                    payload
                );
            },
            clearCurrentRentalExpense() {
                this.currentRentalExpense = null;
            },
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
        },
        watch: {
            propertiesSelected() {
                this.refresh();
            },
        },
    };
</script>
